import { computed, inject, Injectable } from '@angular/core';
import {
  Filter,
  ChartType,
  ViewType,
  TableResponse,
  TableData,
  FilterOption,
  apiFilter,
  TABLE_DATA_PROVIDER,
  ResponseData,
  Cell,
  Row,
  TableRequest,
  CustomTableCell,
} from '../../components/table/table.interface';
import { signal } from '@angular/core';
import { v4 } from 'uuid';
import { ConfigService } from '../config.service';
import {
  map,
  Observable,
  tap,
  of,
  catchError,
  finalize,
  timeout,
  Subscription,
} from 'rxjs';
import { TableFormatService } from './table-format.service';
import { TABLE_CONFIG } from '../../components/table/table-config';
import { BarChartData } from '../../interfaces/global/chart';
import { SnackbarService } from '../snackbar.service';
import { environment } from '../../../environments/environment';

export const applyTableRequestConfig = (
  params: TableRequest,
  options: {
    skipDateRange: boolean;
    timeRange: Pick<TableRequest, 'from' | 'to'>;
    extraBody?: Record<string, unknown>;
    skipFilters: boolean;
  },
): TableRequest => ({
  ...params,
  ...(options.skipDateRange ? {} : options.timeRange),
  ...options.extraBody,
  skipFilters: options.skipFilters,
});

export const resolveTableRowHeight = (customCells: CustomTableCell[]): number =>
  Math.max(60, ...customCells.map((cell) => cell.rowHeight ?? 60));

export const findCustomTableCell = (
  customCells: CustomTableCell[],
  columnName: string,
): CustomTableCell | undefined =>
  customCells.find((cell) => cell.column === columnName);

@Injectable()
export class TableContext {
  // -------- Services injected ------------------

  dataProvider = inject(TABLE_DATA_PROVIDER);
  configService = inject(ConfigService);
  tableFormatService = inject(TableFormatService);
  private configData = inject(TABLE_CONFIG);
  snackBarService = inject(SnackbarService);

  private readonly TIMEOUT = 200;
  private activeSubscription?: Subscription;
  private readonly REQUEST_TIMEOUT = 60000;
  // -------- Shared State Signals --------
  private _tableData = signal<TableData>({
    heads: [],
    rows: [],
    chartType: 'bar',
  });

  private _initialTableData = signal<TableData>({
    heads: [],
    rows: [],
    chartType: 'bar',
  });
  private _initialTotalCount = signal<number>(0);
  private _page = signal<number>(1);
  private _pages = signal<number>(1);
  private _totalCount = signal<number>(0);
  private _filters = signal<Filter>({});
  private _viewType = signal<ViewType>('table');
  private _chartType = signal<ChartType>('bar');
  private _headName = signal<string>('');
  private _pageSize = signal<number>(
    Number(window.localStorage.getItem('tableCount')) || 10,
  );
  private _isLoading = signal<boolean>(true);
  private _searchQuery = signal<string>('');
  private _isTableSorted = signal<boolean>(false);
  private _chartData = signal<BarChartData>({});
  private _initialChartData = signal<BarChartData>({});
  private _isTableInitialized = signal<boolean>(false);
  private _isChartInitialized = signal<boolean>(false);
  private _timeout: ReturnType<typeof setTimeout> | undefined = undefined;
  private _isError = signal<boolean>(false);
  // ----------- Reactive state ------------------

  readonly endpoint = this.configData.endpoint;
  readonly name = this.configData.name;
  readonly controls = this.configData.controls;
  readonly static = this.configData.static;
  readonly canExport = this.configData.canExport;
  readonly canCopy = this.configData.canCopy;
  readonly canSelect = this.configData.canSelect;
  readonly groupCells = this.configData.groupCells;
  readonly hasIndexCol = this.configData.hasIndexCol;
  readonly actionCol = this.configData.actionCol;
  readonly selectOptions = this.configData.selectOptions;
  readonly chartColumn = this.configData.chartColumn;
  readonly coloring = this.configData.coloring;
  readonly customCells = this.configData.customCells;
  readonly onRowClick = this.configData.onRowClick;
  readonly hasChartView = this.configData.hasChartView;
  readonly extraBody = this.configData.extraBody;
  readonly skipFilters = this.configData.skipFilters;
  readonly skipDateRange = this.configData.skipDateRange;
  readonly defaultSort = this.configData.defaultSort;
  readonly DeletedRowsStyle = this.configData.DeletedRowsStyle;

  readonly tableData = this._tableData.asReadonly();
  readonly page = this._page.asReadonly();
  readonly totalCount = this._totalCount.asReadonly();
  readonly filters = this._filters.asReadonly();
  readonly viewType = this._viewType.asReadonly();
  readonly chartType = this._chartType.asReadonly();
  readonly pages = this._pages.asReadonly();
  readonly headName = this._headName.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly searchQuery = this._searchQuery.asReadonly();
  readonly isTableSorted = this._isTableSorted.asReadonly();
  readonly chartData = this._chartData.asReadonly();
  readonly isTableInitialized = this._isTableInitialized.asReadonly();
  readonly isError = this._isError.asReadonly();

  readonly rowHeight = computed(() => resolveTableRowHeight(this.customCells()));

  getCustomCell = (columnName: string) =>
    findCustomTableCell(this.customCells(), columnName);

  resolveAttachmentUrl = (attachment: string) => {
    try {
      if (!attachment) return '';
      const cleaned = attachment.trim();
      const leavePathRegex = /LeaveManagement/i;
      const base = leavePathRegex.test(cleaned) ? environment.leaveManagementCdn : environment.baseUrl;
      return new URL(cleaned, base).toString();
    } catch (e) {
      console.error('resolveAttachmentUrl error', e);
      return attachment;
    }
  };

  getAttachmentName = (attachment: string) => {
    const sanitizedAttachment = attachment.split('?')[0];
    const fileName = sanitizedAttachment.split('/').pop();

    return fileName || attachment;
  };

  getAttachmentValues = (cell: Cell) => {
    if (Array.isArray(cell)) return cell;
    if (typeof cell === 'string' && cell) return [cell];

    return [];
  };

  openAttachment = (attachment: string, event?: Event) => {
    event?.stopPropagation();
    window.open(this.resolveAttachmentUrl(attachment), '_blank', 'noopener,noreferrer');
  };

  downloadAttachment = async (attachment: string, event?: Event) => {
    event?.stopPropagation();
    event?.preventDefault?.();

    const url = this.resolveAttachmentUrl(attachment);
    const fileName = this.getAttachmentName(attachment);

    try {
      const resp = await fetch(url, { mode: 'cors' });
      if (!resp.ok) throw new Error('Network response was not ok');
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.rel = 'noopener noreferrer';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Release memory
      URL.revokeObjectURL(blobUrl);
    } catch (e) {
      // Fallback to direct anchor download (may open in new tab for cross-origin)
      try {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.rel = 'noopener noreferrer';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error('downloadAttachment fallback failed', err);
      }
    }
  };

  private handleRequest<T>(
    obs: Observable<T>,
    errorMessage?: string,
  ): Observable<T> {
    return obs.pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError((error) => {
        console.error(error);
        const message =
          errorMessage ||
          (error.name === 'TimeoutError' ? 'Request timeout' : error);
        this.handleError(message);
        throw error;
      }),
      finalize(() => this.setIsLoading(false)),
    );
  }
  // ----------- Setters ------------------
  setFilters = (filters: Filter) => {
    this._filters.set(filters);
  };

  changeTableView = (type: ViewType) => {
    // Only chart view checks for uninitialized state
    // as table view is the default view and gets initialized on component load
    if (type == 'chart') {
      this._isLoading.set(true);
      if (!this._isChartInitialized()) {
        this.handleRequest(this.getAllData(), 'ChartError').subscribe({
          next: (tableData) => {
            this.constructChartData(tableData);
            // structured clone used to avoid pointing to the same object reference
            this._initialChartData.set(structuredClone(this._chartData()));
            this._isChartInitialized.set(true);
          },
        });
      } else {
        setTimeout(() => {
          this.setIsLoading(false);
        }, 200);
      }
    }
    this._viewType.set(type);
  };

  setPageSize = (newSize: number) => {
    this._pageSize.set(newSize);
    window.localStorage.setItem('tableCount', String(newSize));
  };

  get pageSize() {
    return this._pageSize();
  }

  setPage = (currPage: number) => {
    this._page.set(currPage);
  };

  setIsLoading = (loading: boolean) => {
    if (loading) {
      this._timeout = setTimeout(() => {
        this._isLoading.set(true);
        clearTimeout(this._timeout);
      }, this.TIMEOUT);
      return;
    }

    clearTimeout(this._timeout);
    this._isLoading.set(false);
  };

  setSearchQuery = (query: string) => {
    this._searchQuery.set(query);
  };

  buildRequest = (params: TableRequest): TableRequest =>
    applyTableRequestConfig(params, {
      skipDateRange: this.skipDateRange?.() ?? false,
      timeRange: this.timeRange(),
      extraBody: this.extraBody?.(),
      skipFilters: this.skipFilters?.() ?? false,
    });

  updatePageSize = (newSize: number) => {
    this.setIsLoading(true);

    const record = this.getSortingRecord();

    this.handleRequest(
      this.dataProvider.fetchData(this.buildRequest({
        search: this.searchQuery() !== '' ? this.searchQuery() : undefined,
        page: 1,
        pageSize: newSize,
        filters: this._appliedFilters(),
        sortBy: this.headName() ? [record] : undefined,
      })),
      'PageSizeError',
    ).subscribe({
      next: (res) => {
        this.setPageSize(newSize);
        this.updateTableData(res);
      },
    });
  };

  handleRowChecked = (rowIndex: number, $event: Event) => {
    const checked = ($event.target as HTMLInputElement).checked;
    const updatedRows = this._tableData().rows.map((row, index) => {
      if (index === rowIndex) {
        return { ...row, selected: checked };
      }
      return row;
    });

    this._tableData.set({
      ...this._tableData(),
      rows: updatedRows,
    });
  };

  handleAllRowsChecked = (checked: boolean) => {
    const updatedRows = this._tableData().rows.map((row) => {
      return { ...row, selected: checked };
    });

    this._tableData.set({
      ...this._tableData(),
      rows: updatedRows,
    });
  };
  // ----------- Computed Values ------------------
  timeRange = computed(() => {
    return this.configService.getApiDateTimeRange();
  });

  selectedRows = computed(() => {
    return this.tableData().rows.filter((row) => row.selected);
  });

  readonly _appliedFilters = computed<apiFilter>(() => {
    let falsy_filters: apiFilter = {};
    Object.entries(this._filters()).forEach(([key, value]) => {
      const filterNames = this._filters()
        [key].filter((option: FilterOption) => {
          return option.checked;
        })
        .map((option) => option.value);

      if (filterNames.length < this._filters()[key].length)
        falsy_filters = {
          ...falsy_filters,
          [key]: filterNames,
        };
    });
    return falsy_filters;
  });

  readonly hasActiveFilters = computed((): boolean => {
    return (
      this._appliedFilters() && Object.keys(this._appliedFilters()).length > 0
    );
  });

  readonly iteratableFilters = computed(() => {
    return Object.entries(this._filters());
  });

  // ----------- Public Functions ------------------
  initialize = () => {
    this._headName.set('');
    this._isLoading.set(true);
    
    // Optional reset used when  when table reset button clicked

    // clearing sorting arrow if reset was clicked:
    this._isTableSorted.set(false);

    this.setFilters({});
    return this.handleRequest(
      this.dataProvider.fetchData(this.buildRequest({
        pageSize: this.pageSize,
        page: 1,
        getAvailableFilters: !this._isTableInitialized(),
        filters: this._appliedFilters(),
        getTotalCount: true,
        ...(this.defaultSort() ? { sortBy: [this.defaultSort()!] } : {}),
      })),
      'InitializeError',
    ).pipe(
      tap((res) => {
        this.updateTableData(res);
        if (!this._isTableInitialized()) {
          const dataCopy = structuredClone(this.tableData());
          this._initialTableData.set(structuredClone(dataCopy));
          this._isTableInitialized.set(true);
        }
      }),
    );
  };

  refreshCurrentView = (): Observable<TableResponse> => {
    this.setIsLoading(true);
    const record = this.getSortingRecord();
    const sortBy = this.headName()
      ? [record]
      : this.defaultSort()
        ? [this.defaultSort()!]
        : undefined;

    return this.handleRequest(
      this.dataProvider.fetchData(this.buildRequest({
        search: this.searchQuery() !== '' ? this.searchQuery() : undefined,
        page: this.page(),
        pageSize: this.pageSize,
        filters: this._appliedFilters(),
        sortBy,
        getTotalCount: true,
        getAvailableFilters: false,
      })),
      'RefreshError',
    ).pipe(tap((response) => this.updateTableData(response)));
  };

  resetAppliedFilters = () => {
    const resetFilters: Filter = {};
    Object.entries(this._filters()).forEach(([key, options]) => {
      resetFilters[key] = options.map((option) => ({
        ...option,
        checked: true,
        selected: true,
      }));
    });
    this.setFilters(resetFilters);
  };

  reset = () => {
    this.setIsLoading(true);

    this._isTableSorted.set(false);
    this._headName.set('');
    this.setSearchQuery('');
    this._tableData.set(this._initialTableData());
    this._chartData.set(this._initialChartData());

    this._page.set(1);
    this._pages.set(Math.ceil(this._initialTotalCount() / this.pageSize));
    this.resetAppliedFilters();
    this.setIsLoading(false);
  };

  getSubColumnCell = (
    rowIndex: number,
    colName: string,
    subColName: string,
  ): string | undefined => {
    let row = this.tableData().rows[rowIndex];
    if (typeof row.value !== 'object') return '';
    let cell = row.value[colName] as Record<string, string>;
    return cell[subColName];
  };

  handleFilterChange = (checkedList: string[]) => {
    if (checkedList.length === 0) {
      this._chartData.set({});
      this._totalCount.set(0);
      this._pages.set(0);
      this._page.set(1);
      this._tableData.set({
        ...this._tableData(),
        rows: [],
      });
      return;
    }

    this.setIsLoading(true);
    this.handleRequest(
      this.dataProvider.fetchData(this.buildRequest({
        search: this.searchQuery() !== '' ? this.searchQuery() : undefined,
        page: 1,
        pageSize: this.pageSize,
        filters: this._appliedFilters(),
        getTotalCount: true,
        getAvailableFilters: false,
        returnAll: this.viewType() === 'chart' ? true : false,
      })),
      'FiltersError',
    ).subscribe({
      next: (res) => {
        this.constructChartData(this.createTableInterface(res.data));
        this.updateTableData(res);
      },
    });
  };

  updateCurrentPage = (page: number) => {
    if (page > 0 && page <= this.pages()) {
      console.log(this.pageSize);
      this.setIsLoading(true);

      const record = this.getSortingRecord();

      this.handleRequest(
        this.dataProvider.fetchData(this.buildRequest({
          search: this.searchQuery() !== '' ? this.searchQuery() : undefined,
          page: page,
          sortBy: this.headName() ? [record] : undefined,
          pageSize: this.pageSize,
          getTotalCount: true,
          filters: this._appliedFilters(),
        })),
        'UpdatePageError',
      ).subscribe({
        next: (res) => {
          this.updateTableData(res);
        },
      });
    }
  };

  /**
   * @description updates table data after fetching from server
   * @param {TableResponse} res - response from server
   */
  updateTableData = (res: TableResponse) => {
    let { page, filters, schema, totalCount, pages, rows } = res.data;

    this._page.set(page);

    if (totalCount !== undefined && totalCount !== null) {
      this._totalCount.set(totalCount);
    }

    if (!this.isTableInitialized()) {
      this._initialTotalCount.set(totalCount!);
    }

    this._pages.set(Math.ceil(this.totalCount() / this.pageSize));

    if (this._page() > this._pages()) this._page.set(this._pages());

    this._tableData.set({
      heads: schema.map((col, index) => {
        return {
          name: col.name,
          type: col.type,
          dataType: col.type === 'single' ? col.dataType : undefined,
          asc: this.tableData().heads[index]?.asc,
          ignore: col.ignore,
          groupColumns: col.type === 'group' ? col.groupColumns : undefined,
        };
      }),
      rows: rows.map((row) => {
        return {
          id: v4(),
          value: row,
          selected: false,
        };
      }),
      chartType: this.chartType(),
    });

    if (filters) this.updateFilters(filters);

    this.tableFormatService.resizeColumns(
      this.tableData(),
      this.canSelect(),
      this.hasIndexCol(),
      this.actionCol(),
      this.customCells(),
    );

    this._isError.set(false);
  };

  private updateFilters = (newFilters: apiFilter) => {
    const currFilters = this.iteratableFilters();
    const isFirstTime = currFilters.length === 0;
    const formattedFilters: Filter = Object.fromEntries(
      Object.entries(newFilters as Record<string, string[]>).map(
        ([key, value]: [string, string[]]) => [
          key,
          isFirstTime
            ? value.map((val) => ({
                id: v4(),
                value: val,
                checked: true,
                selected: false,
              }))
            : this._filters()[key].map((option) => ({
                id: option.id,
                value: option.value,
                checked: option.checked && value.includes(option.value),
                selected: false,
              })),
        ],
      ),
    ) as Filter;
    this._filters.set(formattedFilters);
  };

  sortTable = (headName: string) => {
    let header = this._tableData().heads.find((head) => head.name === headName);
    if (!header || header.type === 'group' || header.dataType === 'link') {
      return;
    }

    this._headName.set(headName);
    this.setIsLoading(true);
    headName = headName;
    if (header) {
      const record: Record<string, 'asc' | 'desc'> = {
        [headName]: header.asc ? 'desc' : 'asc',
      };

      this.handleRequest(
        this.dataProvider.fetchData(this.buildRequest({
          search: this.searchQuery() !== '' ? this.searchQuery() : undefined,
          pageSize: this.pageSize,
          page: this.page(),
          sortBy: [record],
          filters: this._appliedFilters(),
        })),
        'SortingError',
      ).subscribe({
        next: (res) => {
          this._isTableSorted.set(true);
          const { schema, rows } = res.data;
          this._tableData.set({
            heads: schema.map((col, index) => {
              return {
                name: col.name,
                type: col.type,
                dataType: col.type === 'single' ? col.dataType : undefined,
                asc:
                  col.name === headName
                    ? !header.asc
                    : this.tableData().heads[index]?.asc,
                ignore: col.ignore,
                groupColumns:
                  col.type === 'group' ? col.groupColumns : undefined,
              };
            }),
            rows: rows.map((row) => {
              return {
                id: v4(),
                value: row,
                selected: false,
              };
            }),
            chartType: this.chartType(),
          });
        },
        error: () => {
          this._headName.set('');
        },
      });
    }
  };

  createTableInterface(data: ResponseData): TableData {
    let { schema, rows } = data;

    return {
      heads: schema.map((col) => {
        return {
          type: col.type,
          name: col.name,
          asc: false,
          ignore: col.ignore,
          dataType: col.type === 'single' ? col.dataType : undefined,
          groupColumns: col.type === 'group' ? col.groupColumns : undefined,
        };
      }),
      rows: rows.map((row) => {
        return {
          id: v4(),
          value: row,
          selected: false,
        };
      }),
      chartType: this.tableData().chartType,
    };
  }

  constructChartData(data: TableData) {
    const { heads, rows } = data;
    const chartColumn = this.chartColumn();

    let chartData: BarChartData = {};
    const filteredHeads = heads.filter((head) => {
      return head.dataType === 'numeric' && head.name !== chartColumn;
    });

    rows.forEach((row) => {
      let cell = row.value[chartColumn!];
      if (cell === undefined) cell = '-';
      chartData[cell as string] = filteredHeads.map((head) => {
        return {
          name: head.name,
          value: Number(row.value[head.name] as string),
        };
      });
    });

    this._chartData.set(chartData);
  }

  getAllData(): Observable<TableData> {
    const filters = this._appliedFilters();

    let hasEmptyFilter = false;
    Object.values(filters).forEach((val) => {
      if (val.length === 0) {
        hasEmptyFilter = true;
      }
    });

    if (hasEmptyFilter) {
      return of({ heads: [], rows: [], chartType: 'bar' });
    }

    this.setIsLoading(true);

    const record = this.getSortingRecord();

    return this.handleRequest(
      this.dataProvider.fetchData(this.buildRequest({
        page: 1,
        returnAll: true,
        getTotalCount: true,
        filters: this._appliedFilters(),
        sortBy: this.headName() ? [record] : undefined,
      })),
      'getAllDataError',
    ).pipe(
      map((res) => {
        return this.createTableInterface(res.data);
      }),
    );
  }

  private getSortingRecord(): Record<string, 'asc' | 'desc'> {
    let record: Record<string, 'asc' | 'desc'> = {};
    if (this.headName()) {
      let header = this._tableData().heads.find(
        (head) => head.name === this.headName(),
      );
      if (header) {
        record = {
          [this.headName()]: header.asc ? 'asc' : 'desc',
        };
      }
    }
    return record;
  }

  handleError = (err: string) => {
    this.setIsLoading(false);
    this.snackBarService.showError(err);

    if (err === 'InitializeError' || err === 'RefreshError') {
      this._isError.set(true);
    }
  };

  /**
   * Ensures table UI can leave the initial skeleton state when first load fails.
   */
  markInitializedAfterError = () => {
    if (!this._isTableInitialized()) {
      this._isTableInitialized.set(true);
    }
  };
}
